/**
 * Central registry for credential-based Integrations cards (Overview grid +
 * generic credential/setup-guide detail view in pages/Integrations.js).
 *
 * Meta Ads and Google Sheets are NOT here -- they have their own bespoke,
 * fully-built flows (real OAuth, CSV import/export) and stay as hand-written
 * sections in Integrations.js. Everything in this file is a "fill in some
 * fields, hit Save" integration: WhatsApp/SMS-style BSPs, email sending,
 * Google Ads, and AI calling.
 *
 * Each entry is consumed by the generic credential-form renderer, so the
 * shape must stay consistent:
 *   { title, icon, color, provider, description, overviewDescription?,
 *     fields: [{ key, label, type: 'text'|'password'|'select', placeholder?,
 *                help?, options?: [{ value, label }] }],
 *     setupSteps: [string], note? }
 *
 * platformLevel: true  -- OAuth-based integrations (Google, Microsoft, Zoom)
 * whose Client ID/Secret are registered once at platform level by the Super
 * Admin. Company admins see a locked info panel instead of credential fields.
 */

export const CRED_CONFIG = {
  whatsapp: {
    title: "WhatsApp Business API",
    icon: "💬",
    color: "#25d366",
    provider: "Gupshup / 360Dialog / Meta Cloud API",
    description: "Send follow-ups and broadcast campaigns over WhatsApp through a Business API provider. Pick whichever BSP you already use -- the setup steps below adapt to your choice.",
    overviewDescription: "Send follow-up messages and broadcast campaigns via WhatsApp API -- Gupshup, 360Dialog, or Meta Cloud API.",
    note: "Looking for free, personal-number WhatsApp sending with a QR-code login instead? That is a separate, already-connected feature -- go to Settings and WhatsApp. This card is for bulk/API-based sending through a Business Solution Provider (BSP).",
    fields: [
      {
        key: "provider", label: "BSP / Provider", type: "select",
        options: [
          { value: "gupshup",   label: "Gupshup" },
          { value: "360dialog", label: "360Dialog" },
          { value: "meta",      label: "Meta Cloud API (direct)" },
          { value: "other",     label: "Other BSP" },
        ],
        help: "Whichever provider issued your WhatsApp Business API access",
      },
      { key: "phoneId",  label: "Phone Number ID", placeholder: "1234567890123456",  type: "text",     help: "Gupshup: App Source number. 360Dialog/Meta: Phone Number ID" },
      { key: "apiToken", label: "API Key / Token",  placeholder: "sk_live_xxxxxx...", type: "password", help: "Permanent API key or token from your provider dashboard" },
    ],
    setupSteps: [
      "Choose your BSP above -- Gupshup, 360Dialog, and Meta Cloud API all work the same way here",
      "Gupshup: App Dashboard then your app then API Keys. 360Dialog: Hub then API Key. Meta: Developers then WhatsApp then Getting Started",
      "Copy the Phone Number ID (or Source number) tied to your WhatsApp line",
      "Generate a permanent / live API key and paste both values above",
      "Click Save -- you can switch providers later without losing your message history",
    ],
  },

  gmail: {
    title: "Gmail / Google Workspace",
    icon: "📧",
    color: "#ea4335",
    provider: "Google",
    description: "Connect Gmail to sync emails with lead profiles. Track opens, replies and send campaigns. Also powers the per-employee Connect Google button on the Work Report page (Calendar + Gmail read-only scopes), so meetings and email volume can be monitored.",
    platformLevel: true,
    supportsPersonalMode: true,
    oauthProvider: "google",
    fields: [
      { key: "clientId",     label: "Client ID",     placeholder: "xxxx.apps.googleusercontent.com", type: "text",     help: "Google Cloud Console -- APIs and Services -- Credentials" },
      { key: "clientSecret", label: "Client Secret", placeholder: "GOCSPX-xxxx",                     type: "password", help: "Created alongside the Client ID" },
    ],
    setupSteps: [
      "Go to console.cloud.google.com and create or open a project",
      "Enable the Gmail API and Google Calendar API under APIs and Services then Library",
      "Create OAuth 2.0 credentials (Web Application type)",
      "Add the backend callback URL as an Authorised redirect URI in your Google Cloud Console project",
      "Copy the Client ID and Client Secret into the Super Admin Portal then Platform Integrations, then Save",
    ],
    redirectUriProvider: "google",
  },

  outlook: {
    title: "Microsoft Outlook",
    icon: "📨",
    color: "#0078d4",
    provider: "Microsoft",
    description: "Connect Outlook to track emails against lead profiles automatically. Also powers the per-employee Connect Outlook/Teams button on the Work Report page (Calendars.Read + Mail.Read), so meetings and email volume can be monitored.",
    platformLevel: true,
    supportsPersonalMode: true,
    oauthProvider: "microsoft",
    fields: [
      { key: "clientId",     label: "Application (Client) ID", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", type: "text",     help: "Azure portal -- App registrations -- Overview" },
      { key: "clientSecret", label: "Client Secret",           placeholder: "xxxxxxxxxxxxxxxxxxxxxxxx",             type: "password", help: "Azure portal -- App registrations -- Certificates and secrets" },
    ],
    setupSteps: [
      "Go to portal.azure.com then Azure Active Directory then App registrations",
      "Click New registration, choose Accounts in any organizational directory",
      "Add the backend callback URL as a Redirect URI (Web platform) in the app registration",
      "Under Certificates and secrets, create a new Client secret",
      "Copy the Application ID and Secret into the Super Admin Portal then Platform Integrations, then Save",
    ],
    redirectUriProvider: "microsoft",
  },

  zoom: {
    title: "Zoom",
    icon: "🔵",
    color: "#2d8cff",
    provider: "Zoom",
    description: "Lets employees connect their Zoom account from the Work Report page so their meetings show up in the team monitoring report. Note: Zoom only exposes other users past-meeting data via its Dashboard API, which requires a paid Business+ plan.",
    platformLevel: true,
    supportsPersonalMode: true,
    oauthProvider: "zoom",
    fields: [
      { key: "clientId",     label: "Client ID",     placeholder: "xxxxxxxxxxxxxxxxxxxx",             type: "text",     help: "Zoom App Marketplace -- your app -- App Credentials" },
      { key: "clientSecret", label: "Client Secret", placeholder: "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx", type: "password", help: "Same App Credentials page" },
    ],
    setupSteps: [
      "Go to marketplace.zoom.us then Develop then Build App then choose General App (OAuth)",
      "Add the backend callback URL under OAuth then Redirect URL for OAuth, plus as an Allow List entry",
      "Under Scopes, add meeting:read and user:read (and the Dashboard scopes if your plan supports them)",
      "Copy the Client ID and Client Secret into the Super Admin Portal then Platform Integrations, then Save",
      "Activate the app (Internal apps activate instantly; Marketplace-listed apps need review)",
    ],
    redirectUriProvider: "zoom",
  },

  twilio: {
    title: "SMS Gateway (Twilio)",
    icon: "📱",
    color: "#f22f46",
    provider: "Twilio",
    description: "Send SMS messages and campaigns. Get credentials from your Twilio Console.",
    fields: [
      { key: "accountSid", label: "Account SID",  placeholder: "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx", type: "text",     help: "Twilio Console -- Dashboard -- Account Info" },
      { key: "authToken",  label: "Auth Token",   placeholder: "your_auth_token",                   type: "password", help: "Shown in the same Account Info section" },
      { key: "fromNumber", label: "From Number",  placeholder: "+15551234567",                      type: "text",     help: "The Twilio phone number to send from" },
    ],
    setupSteps: [
      "Sign up or log in at twilio.com/console",
      "Purchase an SMS-enabled phone number",
      "Copy your Account SID and Auth Token from the Dashboard",
      "Paste all three values above and click Save",
    ],
  },

  email: {
    title: "Email Sending",
    icon: "✉️",
    color: "#7c3aed",
    provider: "SendGrid / SES / SMTP relay",
    description: "Send from your own domain (e.g. hello@yourcompany.com) instead of a generic address. Verify your domain DKIM/SPF records once below, then send campaigns and follow-ups that land in the inbox, not spam.",
    overviewDescription: "Send from your own domain with DKIM/SPF verification, quota tracking, and deliverability built in.",
    fields: [
      { key: "fromDomain", label: "Sending Domain",   placeholder: "mail.yourcompany.com",  type: "text",     help: "The domain your emails will be sent from" },
      { key: "fromEmail",  label: "From Address",     placeholder: "hello@yourcompany.com", type: "text",     help: "The address recipients will see" },
      { key: "apiKey",     label: "Provider API Key", placeholder: "SG.xxxxxxxx...",        type: "password", help: "From SendGrid, Amazon SES, or your SMTP relay provider" },
    ],
    setupSteps: [
      "Pick an email-sending provider -- SendGrid and Amazon SES are the most common",
      "Add and verify your sending domain in their dashboard (this generates DKIM/SPF DNS records)",
      "Add those DKIM/SPF/CNAME records to your domain DNS -- your registrar (GoDaddy, Namecheap, Cloudflare, etc.)",
      "Use the Verify DNS Records check below to confirm the records are live (DNS changes can take a few hours)",
      "Paste your domain, from-address, and API key above, then Save",
    ],
  },

  googleAds: {
    title: "Google Ads",
    icon: "🟢",
    color: "#4285f4",
    provider: "Google",
    description: "Import campaign performance and push offline conversions back to Google Ads so you can see which campaigns actually produce enrolled leads, not just clicks.",
    overviewDescription: "Sync campaign performance and offline conversions with Google Ads.",
    fields: [
      { key: "customerId",     label: "Customer ID",         placeholder: "123-456-7890",                    type: "text",     help: "Top-right of your Google Ads account, format XXX-XXX-XXXX" },
      { key: "developerToken", label: "Developer Token",     placeholder: "xxxxxxxxxxxxxxxxxxxxxx",          type: "password", help: "Google Ads API Center -- your Manager account" },
      { key: "clientId",       label: "OAuth Client ID",     placeholder: "xxxx.apps.googleusercontent.com", type: "text",     help: "Google Cloud Console -- Credentials" },
      { key: "clientSecret",   label: "OAuth Client Secret", placeholder: "GOCSPX-xxxx",                    type: "password", help: "Created alongside the Client ID" },
      { key: "refreshToken",   label: "Refresh Token",       placeholder: "1//0xxxxxxxxxxxx",               type: "password", help: "Generated once via Google OAuth Playground or your own consent flow" },
    ],
    setupSteps: [
      "Get a Google Ads Manager (MCC) account and apply for a Developer Token via the API Center",
      "In Google Cloud Console, enable the Google Ads API and create OAuth 2.0 credentials",
      "Run through the OAuth consent flow once to mint a Refresh Token (Google OAuth Playground works for testing)",
      "Copy your Customer ID from the top-right of the Google Ads UI (remove the dashes)",
      "Paste all five values above and Save -- this card stays credential-only until you plug in real access",
    ],
  },

  aiCalling: {
    title: "AI Calling",
    icon: "🤖",
    color: "#f97316",
    provider: "Sreegen / Bolna / Vapi / Retell AI / your provider",
    description: "Connect any AI voice-calling platform to auto-dial leads with a conversational agent and log call outcomes back to the CRM as activity. Not locked to one vendor -- pick whichever provider you use.",
    overviewDescription: "Auto-dial leads with AI voice agents -- works with Sreegen, Bolna, Vapi, Retell AI, or your provider of choice.",
    fields: [
      {
        key: "provider", label: "AI Calling Provider", type: "select",
        options: [
          { value: "sreegen", label: "Sreegen" },
          { value: "bolna",   label: "Bolna" },
          { value: "vapi",    label: "Vapi" },
          { value: "retell",  label: "Retell AI" },
          { value: "plivo",   label: "Plivo Voice AI" },
          { value: "other",   label: "Other / Custom" },
        ],
        help: "Any conversational AI voice-calling platform -- not exclusive to one provider",
      },
      { key: "apiKey",      label: "API Key / Access Token",  placeholder: "sk_live_xxxxxx...",          type: "password", help: "From your provider developer or integrations settings" },
      { key: "agentId",     label: "Agent / Campaign ID",     placeholder: "agent_xxxxxxxx (optional)",  type: "text",     help: "The specific voice agent or calling campaign to use, if your provider supports multiple" },
      { key: "callbackUrl", label: "Callback / Webhook URL",  placeholder: "Paste into your provider webhook settings", type: "text", help: "Use the Custom Webhook URL below so call outcomes sync back as lead activity" },
    ],
    setupSteps: [
      "Choose your AI calling provider above -- Sreegen, Bolna, Vapi, Retell AI, and most others follow a similar flow",
      "In their dashboard, create a calling agent / assistant and give it a script or use case (lead qualification, appointment booking, etc.)",
      "Copy the API key from their developer or integrations settings",
      "If they support outbound webhooks, paste your CRM Custom Webhook URL (further down this page) so call outcomes log back automatically",
      "Paste your API key (and Agent/Campaign ID, if applicable) above and click Save",
    ],
  },
};

// Lightweight status classifier for the Overview filter chips (All / Connected
// / Pending / Available). "Pending" = credentials were saved at some point
// but the integration is not currently marked connected.
export function getCredStatus(savedEntry) {
  if (!savedEntry) return "available";
  if (savedEntry.connected) return "connected";
  const hasAnyValue = Object.keys(savedEntry).some(k => k !== "connected" && savedEntry[k]);
  return hasAnyValue ? "pending" : "available";
}
